import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'react-daisyui';
import Modal, { ModalProps } from '@/components/modals/Modal/Modal.tsx';
import Radio from '@/components/functional/RadioInput/Radio.tsx';
import CheckboxInputBlock from '@/components/functional/CheckboxInputBlock/CheckboxInputBlock.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';
import ChangeActivityReason, { allExperienceReasons } from '$/_const/trip/ChangeActivityReason.ts';
import type { ChangeActivityReason as ChangeActivityReasonT } from '$/_const/trip/ChangeActivityReason.ts';
import { ChangeActivityRequest, changeActivityRequestSchema } from '$/requests/ChangeActivityRequest.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import { useTripContext } from '@/context/TripContext.tsx';

type ChangeActivityModalProps = ModalProps & {
  dayToRefine: number;
};

const ChangeActivityModal: FC<ChangeActivityModalProps> = ({ onClose, dayToRefine, ...modalProps }) => {
  const { t } = useTranslation();
  const { isLoadingAction, handleRefineDay } = useTripContext();

  const { control, handleSubmit } = useForm<ChangeActivityRequest>({
    resolver: zodResolver(changeActivityRequestSchema),
  });

  const selectedReason = useWatch({ control, name: 'reason' });
  const isSomethingElse = selectedReason === ChangeActivityReason.SOMETHING_ELSE;

  const handleFormSubmit = (data: ChangeActivityRequest) => {
    if (isLoadingAction) return;

    handleRefineDay(dayToRefine, data);

    onClose?.();
  };

  return (
    <Modal.Wrapper wrapperClassName="w-[400px]" {...modalProps} onClose={onClose} showCloseButton={false} closeable>
      <Modal.Title>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="font-feature font-bold italic text-2xl">{t('changeActivityModal.title')}</h2>
        </div>
      </Modal.Title>

      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="reason"
          render={({ field: { onChange, value }, formState: { errors } }) => (
            <Radio.Group<ChangeActivityReasonT> value={value} onChange={onChange} error={errors.reason?.message}>
              {allExperienceReasons.map((option) => (
                <Radio.Input
                  key={option}
                  value={option}
                  render={(props) => (
                    <CheckboxInputBlock
                      {...props}
                      type="radio"
                      label={t(
                        `enum_translations.ChangeActivityReason.${option}`,
                        missingTranslationToReadable(option)
                      )}
                    />
                  )}
                />
              ))}
            </Radio.Group>
          )}
        />

        {isSomethingElse && (
          <Controller
            control={control}
            name="comment"
            render={({ field, formState: { errors } }) => (
              <TextArea
                {...field}
                rows={4}
                placeholder={t('placeholder.what_to_change_comment')}
                error={errors.comment?.message}
              />
            )}
          />
        )}

        <Modal.Nav>
          {({ handleClose }) => (
            <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit(handleFormSubmit)}>
              <LoadingButton loading={isLoadingAction} type="submit" color="primary" className="w-full">
                {t('button.refine_the_day')}
              </LoadingButton>
              <Button type="button" color="ghost" className="text-primary" onClick={handleClose}>
                {t('button.cancel')}
              </Button>
            </form>
          )}
        </Modal.Nav>
      </div>
    </Modal.Wrapper>
  );
};

export default ChangeActivityModal;
